import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import DepositTable from "./DepositTable";
import RipplingPayrollTable from "./RipplingPayrollTable";
import BankAccountFilter from "./BankAccountFilter";
import PaymentPercentInput from "./PaymentPercentInput";
import ConfirmSubmitModal from "./ConfirmSubmitModal";

const ExpenseTable = ({ initialFilterStartDate, initialFilterEndDate }) => {
    const [expenseTransactions, setExpenseTransactions] = useState([]);
    const [grantList, setGrantList] = useState([]);
    const [grantNicknames, setGrantNicknames] = useState({});
    const [showConfirmedRecords, setShowConfirmedRecords] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const fileInputRef = useRef(null);
    const [customizedCategories, setCustomizedCategories] = useState([]);
    const [categoriesByExpenseType, setCategoriesByExpenseType] = useState({});
    const [availableBankAccounts, setAvailableBankAccounts] = useState([]);
    const [selectedBankAccounts, setSelectedBankAccounts] = useState([]);
    const [expenseTypes, setExpenseTypes] = useState([]);
    const fixedCategories = [
        "Materials", "Overhead", "Travel", "Fee", "Equipment",
        "Sub-contract", "Internal Bank Transfer", "Credit Card Payment",
        "N/A (Non-grant Item)",
    ];
    const categories = [...new Set([...fixedCategories, ...expenseTypes])];
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [expensePeriod, setExpensePeriod] = useState("all");
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const [showCustomPeriodPopup, setShowCustomPeriodPopup] = useState(false);
    const [depositTransactions, setDepositTransactions] = useState([]);
    const [ripplingPayrollTransactions, setRipplingPayrollTransactions] =
        useState([]);
    const [payrollRelatedExpenses, setPayrollRelatedExpenses] = useState([]);

    // Add these state variables after your existing useState declarations
    const [showEditNotePopup, setShowEditNotePopup] = useState(false);
    const [transactionToEditNote, setTransactionToEditNote] = useState(null);
    const [showBankColumns, setShowBankColumns] = useState(false);

    // Itemization state
    const [expandedItemRows, setExpandedItemRows] = useState({});

    const toggleItemization = (transactionId) => {
        setExpandedItemRows(prev => ({ ...prev, [transactionId]: !prev[transactionId] }));
    };

    const getLineItems = (transaction) => {
        try {
            return JSON.parse(transaction["Line Items"] || "[]");
        } catch { return []; }
    };

    const addLineItem = async (transaction) => {
        const items = getLineItems(transaction);
        const newItem = {
            id: Date.now().toString(),
            name: '',
            description: '',
            amount: '',
            grantCategory: '',
            customizedCategory: 'N/A',
            payment: {}
        };
        items.push(newItem);
        await saveLineItems(transaction, items);
    };

    const updateLineItem = async (transaction, itemId, field, value) => {
        const items = getLineItems(transaction);
        const idx = items.findIndex(i => i.id === itemId);
        if (idx === -1) return;
        items[idx][field] = value;
        await saveLineItems(transaction, items);
    };

    const removeLineItem = async (transaction, itemId) => {
        const items = getLineItems(transaction).filter(i => i.id !== itemId);
        await saveLineItems(transaction, items);
    };

    const saveLineItems = async (transaction, items) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/expense/${transaction.ID}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ ...transaction, "Line Items": JSON.stringify(items) }),
                }
            );
            if (response.ok) {
                setExpenseTransactions(prev =>
                    prev.map(t => t.ID === transaction.ID ? { ...t, "Line Items": JSON.stringify(items) } : t)
                );
            }
        } catch (error) {
            console.error("Error saving line items:", error);
        }
    };

    // Apply initial filter when provided
    useEffect(() => {
        if (initialFilterStartDate && initialFilterEndDate) {
        setCustomStartDate(initialFilterStartDate);
        setCustomEndDate(initialFilterEndDate);
        setExpensePeriod('custom');
        // No need to show popup since we're setting dates programmatically
        setShowCustomPeriodPopup(false);
        }
    }, [initialFilterStartDate, initialFilterEndDate]);

    // Add this handler function
    const handleEditNoteClick = (transaction) => {
        setTransactionToEditNote(transaction);
        setShowEditNotePopup(true);
    };

    // Add this handler for saving the note
    const handleSaveNote = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/expense/note/${transactionToEditNote.ID}`,
                {
                    method: "PUT",
                    credentials: 'include',
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ Note: transactionToEditNote.Note }),
                }
            );

            if (response.ok) {
                // Update the local state to reflect the changes
                const updatedTransactions = filteredTransactions.map((t) =>
                    t.ID === transactionToEditNote.ID
                        ? { ...t, Note: transactionToEditNote.Note }
                        : t
                );
                setFilteredTransactions(updatedTransactions);
                setShowEditNotePopup(false);
            } else {
                throw new Error("Failed to update note");
            }
        } catch (error) {
            console.error("Error updating note:", error);
            alert("Error updating note.");
        }
    };

    // Fetch bank accounts from the company endpoint (using unified /company/banks)
    const fetchBankAccounts = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/company/banks`,
                {
                    credentials: 'include'
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            const banks = data.banks || [];
            const accounts = [];
            
            // Convert flat banks array to account list
            banks.forEach((bank, index) => {
                const bankName = bank.bankName;
                const accountType = bank.accountType || 'Checking';
                const accountNumber = bank.accountNumber || '';
                const lastFour = accountNumber.slice(-4) || accountNumber;
                
                // Include both checking and debit accounts for ExpenseTable
                const accountTypeLower = accountType.toLowerCase();
                if (accountTypeLower === 'checking' || accountTypeLower === 'debit') {
                    accounts.push({
                        bank: bankName,
                        accountNumber: `****${lastFour}`,
                        accountType: 'Checking', // Normalize display name
                        key: `${bankName.toLowerCase()}|****${lastFour}|checking`
                    });
                }
            });
            
            setAvailableBankAccounts(accounts);
            // Select all accounts by default
            setSelectedBankAccounts(accounts.map(acc => acc.key));
        } catch (error) {
            console.error("Error fetching bank accounts:", error);
        }
    }, []);

    const fetchExpenseTransactions = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/expense`,
                {
                    credentials: 'include'
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            console.log("Fetched expense transactions:", data);

            const sortedData = data.sort(
                (a, b) => new Date(b["Date (UTC)"]) - new Date(a["Date (UTC)"])
            );
            setExpenseTransactions(sortedData);
        } catch (error) {
            console.error("Error fetching expense transactions:", error);
        }
    }, []);

    const fetchDepositTransactions = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/expense/deposits`,
                {
                    credentials: 'include'
                }
            );
            if (!response.ok)
                throw new Error(`HTTP error! Status: ${response.status}`);
            const data = await response.json();

            console.log("Fetched deposit transactions:", data);

            setDepositTransactions(data);
        } catch (error) {
            console.error("Error fetching deposit transactions:", error);
        }
    }, []);

    const fetchRipplingPayrollTransactions = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/expense/rippling-payroll`,
                {
                    credentials: 'include'
                }
            );
            if (!response.ok)
                throw new Error(`HTTP error! Status: ${response.status}`);
            const data = await response.json();
            setRipplingPayrollTransactions(data);
        } catch (error) {
            console.error(
                "Error fetching Rippling payroll transactions:",
                error
            );
        }
    }, []);
    // Then define fetchAllTransactions
    const fetchAllTransactions = useCallback(async () => {
        await Promise.all([
            fetchExpenseTransactions(),
            fetchDepositTransactions(),
            fetchRipplingPayrollTransactions(),
        ]);
    }, [
        fetchExpenseTransactions,
        fetchDepositTransactions,
        fetchRipplingPayrollTransactions,
    ]);

    // Use fetchAllTransactions in useEffect
    useEffect(() => {
        fetchAllTransactions();
    }, [fetchAllTransactions]);

    const fetchGrantList = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/grants/grant-list`,
                {
                    credentials: 'include'
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const grantData = await response.json();
            setGrantList(grantData);

            // Also fetch full grant data for nicknames
            try {
                const grantsResponse = await fetch(
                    `${process.env.REACT_APP_BACKEND_API_URL}/grants`,
                    { credentials: 'include' }
                );
                if (grantsResponse.ok) {
                    const fullGrants = await grantsResponse.json();
                    const nickMap = {};
                    fullGrants.forEach(g => {
                        if (g['Grant Nickname']) {
                            nickMap[g['Grant/Contract Number']] = g['Grant Nickname'];
                        }
                    });
                    setGrantNicknames(nickMap);
                }
            } catch (e) {
                console.error("Error fetching grant nicknames:", e);
            }
        } catch (error) {
            console.error("Error fetching grant list:", error);
        }
    }, []);

    const fetchCustomizedCategories = useCallback(async () => {
        try {
            const [flatResponse, groupedResponse] = await Promise.all([
                fetch(
                    `${process.env.REACT_APP_BACKEND_API_URL}/customized-categories`,
                    { credentials: 'include' }
                ),
                fetch(
                    `${process.env.REACT_APP_BACKEND_API_URL}/customized-categories/by-expense-type`,
                    { credentials: 'include' }
                )
            ]);
            if (flatResponse.ok) {
                const categories = await flatResponse.json();
                setCustomizedCategories(categories);
            }
            if (groupedResponse.ok) {
                const grouped = await groupedResponse.json();
                setCategoriesByExpenseType(grouped);
            }
        } catch (error) {
            console.error("Error fetching customized categories:", error);
        }
    }, []);

    const fetchExpenseTypes = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/customized-categories/expense-types`,
                { credentials: 'include' }
            );
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const types = await response.json();
            setExpenseTypes(types);
        } catch (error) {
            console.error("Error fetching expense types:", error);
        }
    }, []);

    useEffect(() => {
        fetchAllTransactions();
        fetchGrantList();
        fetchCustomizedCategories();
        fetchExpenseTypes();
        fetchBankAccounts();
    }, [fetchAllTransactions, fetchGrantList, fetchCustomizedCategories, fetchExpenseTypes, fetchBankAccounts]);

    useEffect(() => {
        filterTransactions();
    }, [
        expenseTransactions,
        showConfirmedRecords,
        expensePeriod,
        customStartDate,
        customEndDate,
        selectedBankAccounts,
    ]);

    const handleExpensePeriodChange = (event) => {
        const selectedPeriod = event.target.value;
        setExpensePeriod(selectedPeriod);
        if (selectedPeriod === "custom") {
            setShowCustomPeriodPopup(true);
        } else {
            setCustomStartDate("");
            setCustomEndDate("");
        }
    };

    const handleCustomPeriodSubmit = () => {
        setShowCustomPeriodPopup(false);
        setExpensePeriod("custom");
    };

    // Check if a transaction is payroll-related (should be shown in RipplingPayrollTable instead)
    const isPayrollRelatedTransaction = (t) => {
        const desc = (t.Description || t['Bank Description'] || '').toLowerCase();
        return desc.includes('rippling') ||
               desc.includes('matrix trust') ||
               (desc.includes('corporate') && (desc.includes('payroll') || desc.includes('tax')));
    };

    const filterTransactions = () => {
        let filtered = expenseTransactions;

        filtered = filtered.filter((t) => t.Confirmed !== "Yes");

        // Separate payroll-related transactions to pass to RipplingPayrollTable
        const payrollRelated = filtered.filter(t => isPayrollRelatedTransaction(t));
        setPayrollRelatedExpenses(payrollRelated);
        filtered = filtered.filter(t => !isPayrollRelatedTransaction(t));

        const currentDate = new Date();
        let startDate, endDate;

        switch (expensePeriod) {
            case "lastYear":
                startDate = new Date(currentDate.getFullYear() - 1, 0, 1);
                endDate = new Date(currentDate.getFullYear() - 1, 11, 31);
                break;
            case "lastQuarter":
                startDate = new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth() - 3,
                    1
                );
                endDate = new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    0
                );
                break;
            case "lastMonth":
                startDate = new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth() - 1,
                    1
                );
                endDate = new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    0
                );
                break;
            case "custom":
                startDate = new Date(customStartDate);
                endDate = new Date(customEndDate);
                // adding one day to end date to temp fix time zone issues?
                endDate.setDate(endDate.getDate() + 1);
                endDate.setHours(23, 59, 59, 999);
                break;
            default:
                if (!isNaN(parseInt(expensePeriod))) {
                    startDate = new Date(parseInt(expensePeriod), 0, 1);
                    endDate = new Date(parseInt(expensePeriod), 11, 31);
                }
        }

        if (startDate && endDate) {
            filtered = filtered.filter((t) => {
                const transactionDate = new Date(t["Date (UTC)"]);
                console.log("transactionDate:", transactionDate);
                console.log("endDate:", endDate);
                console.log("transactiondate <= endDate:", transactionDate <= endDate);
                return (
                    transactionDate >= startDate && transactionDate <= endDate
                );
            });
        }

        console.log("selected bank accounts:", selectedBankAccounts);

        // Bank account filtering - normalize bank type to lowercase for comparison
        if (selectedBankAccounts.length > 0 && availableBankAccounts.length > 0) {
            filtered = filtered.filter((t) => {
                const bank = (t["Bank Type"] || "Unknown").toLowerCase();
                const accountNumber = t["Last Four Digits"] ? `****${t["Last Four Digits"]}` : "N/A";
                const accountKey = `${bank}|${accountNumber}|checking`;
                return selectedBankAccounts.includes(accountKey);
            });
        }

        setFilteredTransactions(filtered);
    };

    const getExpensePeriodOptions = () => {
        const years = [
            ...new Set(
                expenseTransactions.map((t) =>
                    new Date(t["Date (UTC)"]).getFullYear()
                )
            ),
        ];
        return [
            { value: "all", label: "All" },
            ...years.map((year) => ({
                value: year.toString(),
                label: year.toString(),
            })),
            { value: "lastYear", label: "Last Year" },
            { value: "lastQuarter", label: "Last Quarter" },
            { value: "lastMonth", label: "Last Month" },
            { value: "custom", label: "Custom Period" },
        ];
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("bankType", selectedBank);

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_API_URL}/expense/import`,
                    {
                        method: "POST",
                        credentials: 'include',
                        body: formData,
                    }
                );

                const data = await response.json();

                if (
                    response.status === 409 &&
                    data.type === "DUPLICATE_TRANSACTIONS"
                ) {
                    const duplicateStats = {
                        expense: data.duplicates.expense.length,
                        payroll: data.duplicates.payroll.length,
                        deposit: data.duplicates.deposit.length,
                    };

                    const proceed = window.confirm(
                        `Found duplicate transactions:\n` +
                            `- ${duplicateStats.expense} expense transactions\n` +
                            `- ${duplicateStats.payroll} payroll transactions\n` +
                            `- ${duplicateStats.deposit} deposit transactions\n\n` +
                            `Do you want to proceed with importing only the new transactions?`
                    );

                    if (proceed) {
                        const overrideFormData = new FormData();
                        overrideFormData.append("file", file);
                        overrideFormData.append("bankType", selectedBank);
                        overrideFormData.append("override", "true");

                        const overrideResponse = await fetch(
                            `${process.env.REACT_APP_BACKEND_API_URL}/expense/import`,
                            {
                                method: "POST",
                                credentials: 'include',
                                body: overrideFormData,
                            }
                        );

                        if (overrideResponse.ok) {
                            const importStats = await overrideResponse.json();
                            alert(
                                `Import successful:\n` +
                                    `Expense transactions: ${importStats.statistics.expense.imported} imported\n` +
                                    `Payroll transactions: ${importStats.statistics.payroll.imported} imported\n` +
                                    `Deposit transactions: ${importStats.statistics.deposit.imported} imported`
                            );
                            await fetchAllTransactions();
                        } else {
                            throw new Error("Failed to import transactions");
                        }
                    }
                } else if (response.ok) {
                    alert(
                        `Import successful:\n` +
                            `Expense transactions: ${data.statistics.expense.imported} imported\n` +
                            `Payroll transactions: ${data.statistics.payroll.imported} imported\n` +
                            `Deposit transactions: ${data.statistics.deposit.imported} imported`
                    );
                    await fetchAllTransactions();
                } else {
                    throw new Error(
                        data.message || "Failed to import transactions"
                    );
                }
                event.target.value = "";
            } catch (error) {
                console.error("Error importing transactions:", error);
                alert(`Error importing transactions: ${error.message}`);
            }
        }
    };

    // Add state for bank selection
    const [selectedBank, setSelectedBank] = useState("mercury");

    const handleEditSubmit = async (updatedTransaction) => {
        console.log(
            "Sending request to update transaction:",
            updatedTransaction
        );
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/expense/${updatedTransaction.ID}`,
                {
                    method: "PUT",
                    credentials: 'include',
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(updatedTransaction),
                }
            );
            if (response.ok) {
                // Auto-unapprove if the transaction was checked
                const originalTransaction = expenseTransactions.find(t => t.ID === updatedTransaction.ID);
                if (originalTransaction && originalTransaction.Checked === "Yes") {
                    try {
                        await fetch(
                            `${process.env.REACT_APP_BACKEND_API_URL}/expense/check/${updatedTransaction.ID}`,
                            {
                                method: "PUT",
                                credentials: 'include',
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ Checked: false }),
                            }
                        );
                    } catch (uncheckError) {
                        console.error("Error auto-unapproving transaction:", uncheckError);
                    }
                }
                fetchExpenseTransactions();
            } else {
                alert("Failed to update expense transaction.");
            }
        } catch (error) {
            console.error("Error updating expense transaction:", error);
            alert("Error updating expense transaction.");
        }
    };
    const handleEditDefaultPaymentSubmit = async (selectedTransaction) => {
        if (!selectedTransaction) {
            alert("No transaction selected");
            return;
        }

        // Clean the payment: only keep grants that are in the current grant list
        const cleanedPayment = {};
        grantList.forEach((grant) => {
            cleanedPayment[grant] = selectedTransaction.defaultPayment[grant] || "0";
        });
        selectedTransaction.defaultPayment = cleanedPayment;

        try {
            console.log("Sending request to update default payment:", {
                id: selectedTransaction.ID,
                defaultPayment: selectedTransaction.defaultPayment,
            });

            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/expense/defaultPayment/${selectedTransaction.ID}`,
                {
                    method: "PUT",
                    credentials: 'include',
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        id: selectedTransaction.ID,
                        defaultPayment: selectedTransaction.defaultPayment,
                    }),
                }
            );

            if (response.ok) {
                console.log("Default payment updated successfully");
                // Auto-unapprove if the transaction was checked
                const originalTransaction = expenseTransactions.find(t => t.ID === selectedTransaction.ID);
                if (originalTransaction && originalTransaction.Checked === "Yes") {
                    try {
                        await fetch(
                            `${process.env.REACT_APP_BACKEND_API_URL}/expense/check/${selectedTransaction.ID}`,
                            {
                                method: "PUT",
                                credentials: 'include',
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ Checked: false }),
                            }
                        );
                    } catch (uncheckError) {
                        console.error("Error auto-unapproving transaction:", uncheckError);
                    }
                }
                fetchExpenseTransactions();
                // Popup removed - inline editing handles UI refresh
            } else {
                const errorText = await response.text();
                console.error(
                    "Failed to update default payment. Server response:",
                    errorText
                );
                alert(
                    `Failed to update default payment. Server response: ${errorText}`
                );
            }
        } catch (error) {
            console.error("Error updating default payment:", error);
            alert(`Error updating default payment: ${error.message}`);
        }
    };

    const handleCheckboxChange = async (id, checked) => {
        // Validation deferred to the approve step (handleConfirmCheckedRecords)
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/expense/check/${id}`,
                {
                    method: "PUT",
                    credentials: 'include',
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ Checked: checked }),
                }
            );
            if (response.ok) {
                fetchExpenseTransactions();
            } else {
                alert("Failed to update check status.");
            }
        } catch (error) {
            console.error("Error updating check status:", error);
            alert("Error updating check status.");
        }
    };

    // Select-all / clear-all checked status across visible (filtered) transactions.
    // Uses the bulk-update endpoint so the backend rewrites the CSV exactly once —
    // avoids GCS retry-limit-exhausted errors from many concurrent writes.
    const handleSelectAllVisible = async (shouldCheck) => {
        const visibleIds = filteredTransactions
            .filter((t) => t.Confirmed !== "Yes" && t.ID)
            .map((t) => t.ID);
        if (visibleIds.length === 0) return;
        try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/expense/bulk-update`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ids: visibleIds, Checked: shouldCheck }),
            });
            if (!resp.ok) throw new Error('Bulk update failed');
            fetchExpenseTransactions();
        } catch (error) {
            console.error("Error in select-all:", error);
            alert("Error updating check status for visible records.");
        }
    };

    // Bulk-set Expense Type (Grant Category) for ALL available (unconfirmed) visible transactions
    const [showBulkCategoryPopup, setShowBulkCategoryPopup] = useState(false);
    const [bulkCategoryValue, setBulkCategoryValue] = useState("");
    const handleBulkSetCategory = async () => {
        if (!bulkCategoryValue) {
            alert("Please choose an expense type to apply.");
            return;
        }
        const targets = filteredTransactions.filter((t) => t.Confirmed !== "Yes");
        if (targets.length === 0) {
            alert("No unconfirmed transactions to update.");
            return;
        }
        try {
            // Compute the new Default Payment object based on the new category
            // (this matches handleGrantCategoryChange's logic, applied once).
            const nonGrantFundName =
                grantList.find((g) => g.toLowerCase().includes("non-grant")) || "non-grant funds";
            const actualGrants = grantList.filter(
                (g) => g.toLowerCase() !== nonGrantFundName.toLowerCase()
            );
            let newDefaultPayment = null;
            if (bulkCategoryValue === "N/A (Non-grant Item)") {
                newDefaultPayment = {};
                grantList.forEach((g) => { newDefaultPayment[g] = "0"; });
                newDefaultPayment[nonGrantFundName] = "100";
            } else if (actualGrants.length >= 1) {
                newDefaultPayment = {};
                grantList.forEach((g) => {
                    newDefaultPayment[g] = (g === actualGrants[0]) ? "100" : "0";
                });
            }

            const isPaymentClearingCategory = [
                "Internal Bank Transfer",
                "Credit Card Payment",
            ].includes(bulkCategoryValue);

            const ids = targets.map((t) => t.ID).filter(Boolean);
            const body = { ids, grantCategory: bulkCategoryValue };
            if (isPaymentClearingCategory) body.customizedCategory = "N/A";
            if (newDefaultPayment) body.defaultPayment = JSON.stringify(newDefaultPayment);

            const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/expense/bulk-update`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!resp.ok) throw new Error('Bulk update failed');
            fetchExpenseTransactions();
            setShowBulkCategoryPopup(false);
            setBulkCategoryValue("");
        } catch (error) {
            console.error("Error in bulk set category:", error);
            alert("Error setting categories.");
        }
    };

    const handleConfirmCheckedRecords = async () => {
        setShowConfirmModal(false);

        // Validate checked transactions (skip already confirmed)
        const checkedTransactions = expenseTransactions.filter((t) => t.Checked === "Yes");
        const errors = [];
        for (const t of checkedTransactions.filter(t => t.Confirmed !== "Yes")) {
            const desc = `${new Date(t["Date (UTC)"]).toLocaleDateString()} - $${Math.abs(parseFloat(t.Amount)).toFixed(2)}`;
            // Block approval when expense type (Grant Category) is missing
            if (!t["Grant Category"] || String(t["Grant Category"]).trim() === "") {
                errors.push(`${desc}: expense type is required before approval`);
                continue;
            }
            if (["Internal Bank Transfer", "Credit Card Payment", "N/A (Non-grant Item)"].includes(t["Grant Category"])) continue;
            const lineItems = getLineItems(t);
            if (lineItems.length > 0) {
                const itemTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
                const transactionAmount = Math.abs(parseFloat(t.Amount) || 0);
                if (Math.abs(itemTotal - transactionAmount) > 0.01) {
                    errors.push(`${desc}: line item amounts ($${itemTotal.toFixed(2)}) ≠ transaction ($${transactionAmount.toFixed(2)})`);
                }
                for (let i = 0; i < lineItems.length; i++) {
                    if (!lineItems[i].grantCategory) {
                        errors.push(`${desc}: item "${lineItems[i].name || `#${i + 1}`}" has no category assigned`);
                    }
                    const itemPayment = lineItems[i].payment || {};
                    const itemPaymentTotal = grantList.reduce((sum, g) => sum + (parseFloat(itemPayment[g]) || 0), 0);
                    if (Math.abs(itemPaymentTotal - 100) > 0.01) {
                        errors.push(`${desc}: item "${lineItems[i].name || `#${i + 1}`}" grant split = ${itemPaymentTotal.toFixed(1)}% (must be 100%)`);
                    }
                }
            } else {
                const payment = JSON.parse(t["Default Payment"] || "{}");
                const total = grantList.reduce((sum, g) => sum + (parseFloat(payment[g]) || 0), 0);
                if (Math.abs(total - 100) > 0.01) {
                    errors.push(`${desc}: payment total = ${total.toFixed(1)}% (must be 100%)`);
                }
            }
        }

        if (errors.length > 0) {
            alert(`Cannot confirm. The following issues must be fixed:\n\n${errors.join('\n')}`);
            return;
        }

        const checkedIds = checkedTransactions.map((transaction) => transaction.ID);

        console.log("Checked IDs:", checkedIds);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/expense/confirmChecked`,
                {
                    method: "POST",
                    credentials: 'include',
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ checkedIds }),
                }
            );
            if (response.ok) {
                fetchExpenseTransactions();
                alert("Checked records confirmed successfully");
            } else {
                alert("Failed to confirm checked records.");
            }
        } catch (error) {
            console.error("Error confirming checked records:", error);
            alert("Error confirming checked records.");
        }
    };

    const handleAutoFill = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/expense/autofill`,
                {
                    method: "POST",
                    credentials: 'include',
                }
            );
            if (response.ok) {
                fetchExpenseTransactions();
                alert("Auto-fill completed successfully");
            } else {
                alert("Failed to auto-fill data.");
            }
        } catch (error) {
            console.error("Error auto-filling data:", error);
            alert("Error auto-filling data.");
        }
    };

    // Update the handleGrantCategoryChange function
    const handleGrantCategoryChange = async (transaction, newCategory) => {
        console.log("New category selected:", newCategory);
        let updatedTransaction = {
            ...transaction,
            "Grant Category": newCategory,
            "Customized Category": [
                "Internal Bank Transfer",
                "Credit Card Payment",
            ].includes(newCategory)
                ? "N/A"
                : transaction["Customized Category"],
        };

        // Filter out "non-grant funds" to check if only one actual grant exists
        const nonGrantFundName =
            grantList.find((grant) =>
                grant.toLowerCase().includes("non-grant")
            ) || "non-grant funds";

        const actualGrants = grantList.filter(
            (grant) => grant.toLowerCase() !== nonGrantFundName.toLowerCase()
        );

        // Create a default payment object
        const newDefaultPayment = {};

        if (newCategory === "N/A (Non-grant Item)") {
            // Set all grant funds to 0%
            grantList.forEach((grant) => {
                newDefaultPayment[grant] = "0";
            });

            // Set 'Non-Grant Funds' to 100%
            newDefaultPayment[nonGrantFundName] = "100";
        } else if (actualGrants.length >= 1) {
            // For any grant-related category, default 100% to the first grant
            grantList.forEach((grant) => {
                if (grant === actualGrants[0]) {
                    newDefaultPayment[grant] = "100"; // 100% to the first actual grant
                } else {
                    newDefaultPayment[grant] = "0"; // 0% to all others including non-grant funds
                }
            });
        }

        // Only update the default payment if we're handling one of our special cases
        if (
            newCategory === "N/A (Non-grant Item)" ||
            actualGrants.length >= 1
        ) {
            console.log("Updating default payment:", newDefaultPayment);
            updatedTransaction = {
                ...updatedTransaction,
                "Default Payment": JSON.stringify(newDefaultPayment),
            };
        }

        await handleEditSubmit(updatedTransaction);
    };

    return (
        <div className="space-y-6">
            {/* relative z-30 keeps the Bank Accounts drop-down above the table's
                sticky header instead of being clipped by it. */}
            <div className="card p-6 relative z-30">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Expense Transactions</h2>
                
                {/* Filters */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Period</label>
                        <select
                            value={expensePeriod}
                            onChange={handleExpensePeriodChange}
                            className="input-field"
                        >
                            {getExpensePeriodOptions().map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <BankAccountFilter
                        availableAccounts={availableBankAccounts}
                        selectedAccounts={selectedBankAccounts}
                        onSelectionChange={setSelectedBankAccounts}
                        label="Bank Accounts"
                    />
                </div>

                <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                    ref={fileInputRef}
                />

                {/* Actions: bank details, categories and autofill on the left; submit on the right */}
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        onClick={() => setShowBankColumns(!showBankColumns)}
                        className="btn-secondary flex items-center gap-2"
                    >
                        <svg
                            className={`w-4 h-4 transition-transform duration-200 ${showBankColumns ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        {showBankColumns ? 'Hide' : 'Show'} Bank Details
                    </button>

                    <Link to="/customized-categories">
                        <button className="btn-secondary">
                            Edit Expense Type/Subcategory
                        </button>
                    </Link>

                    <button
                        onClick={handleAutoFill}
                        className="btn-secondary"
                    >
                        AutoFill
                    </button>

                    <button
                        onClick={() => setShowConfirmModal(true)}
                        className="ml-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                    >
                        Submit Approved Records
                    </button>
                </div>
            </div>

            <div className="card overflow-hidden relative z-0">
                <div className="expense-table-wrapper overflow-x-auto max-h-[600px] overflow-y-auto" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <style>{`
                        .expense-table-wrapper::-webkit-scrollbar { height: 12px; }
                        .expense-table-wrapper::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 6px; }
                        .expense-table-wrapper::-webkit-scrollbar-thumb { background: #888; border-radius: 6px; }
                        .expense-table-wrapper::-webkit-scrollbar-thumb:hover { background: #555; }
                        .expense-table-wrapper { scrollbar-width: auto; scrollbar-color: #888 #f1f1f1; }
                    `}</style>
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-30">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Date (UTC)</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Amount</th>
                                {showBankColumns && (
                                    <>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Bank</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Account #</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Type</th>
                                    </>
                                )}
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                    <div className="flex flex-col items-start gap-1">
                                        <span>Expense Type</span>
                                        <button
                                            onClick={() => setShowBulkCategoryPopup(true)}
                                            className="text-[10px] px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 normal-case font-medium"
                                            title="Set expense type for all unconfirmed transactions"
                                        >
                                            Bulk Set
                                        </button>
                                    </div>
                                </th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Subcategory</th>
                                <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" colSpan={grantList.length || 1}>
                                    Payment (%)
                                </th>
                                <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky right-0 z-40" style={{ position: 'sticky', right: 0, boxShadow: '-2px 0 4px rgba(0,0,0,0.05)' }}>
                                    <div className="flex flex-col items-center gap-1">
                                        <span>Approve</span>
                                        <button
                                            onClick={() => {
                                                const visibleUnconfirmed = filteredTransactions.filter(t => t.Confirmed !== "Yes");
                                                const allChecked = visibleUnconfirmed.length > 0 && visibleUnconfirmed.every(t => t.Checked === "Yes");
                                                handleSelectAllVisible(!allChecked);
                                            }}
                                            className="text-[10px] px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 normal-case font-medium"
                                            title="Toggle select all visible unconfirmed transactions"
                                        >
                                            Select All
                                        </button>
                                    </div>
                                </th>
                            </tr>
                            <tr>
                                <th className="px-4 py-2 bg-gray-50"></th>
                                <th className="px-4 py-2 bg-gray-50"></th>
                                {showBankColumns && (
                                    <>
                                        <th className="px-4 py-2 bg-gray-50"></th>
                                        <th className="px-4 py-2 bg-gray-50"></th>
                                        <th className="px-4 py-2 bg-gray-50"></th>
                                    </>
                                )}
                                <th className="px-2 py-2 bg-gray-50"></th>
                                <th className="px-2 py-2 bg-gray-50"></th>
                                {grantList.map((grant) => (
                                    <th key={grant} className="px-2 py-2 text-center text-xs font-medium text-gray-700 bg-gray-100 border-l border-gray-200">
                                        {grantNicknames[grant] ? `${grantNicknames[grant]} (${grant})` : grant}
                                    </th>
                                ))}
                                <th className="px-2 py-2 bg-gray-50 sticky right-0 z-20" style={{ position: 'sticky', right: 0 }}></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white">
                        {filteredTransactions.map((transaction, index) => {
                            const payment = JSON.parse(transaction["Default Payment"] || "{}");
                            // Calculate total using only current grant list keys
                            const paymentTotal = grantList.reduce((sum, g) => sum + (parseFloat(payment[g]) || 0), 0);
                            const colCount = 3 + (showBankColumns ? 3 : 0) + (grantList.length || 1) + 1;
                            const isChecked = transaction.Checked === "Yes";
                            const rowBgClass = isChecked ? "bg-primary-50" : (index % 2 === 0 ? "bg-white" : "bg-gray-50");
                            return (
                            <React.Fragment key={transaction.ID}>
                            <tr
                                className={`hover:bg-gray-50 transition-colors duration-150 ${rowBgClass} ${
                                    isChecked ? "border-l-4 border-primary-400" : ""
                                }`}
                            >
                                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                    {new Date(
                                        transaction["Date (UTC)"]
                                    ).toLocaleDateString()}
                                </td>
                                <td
                                    className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                                        parseFloat(transaction.Amount) < 0
                                            ? "text-red-600"
                                            : "text-green-600"
                                    }`}
                                >
                                    $
                                    {Math.abs(
                                        parseFloat(transaction.Amount)
                                    ).toFixed(2)}
                                </td>
                                {showBankColumns && (
                                    <>
                                        <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">{transaction["Bank Type"] || "Unknown"}</td>
                                        <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">{transaction["Last Four Digits"] ? `****${transaction["Last Four Digits"]}` : "N/A"}</td>
                                        <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">Debit</td>
                                    </>
                                )}
                                <td className="px-2 py-2">
                                    {getLineItems(transaction).length > 0 ? (
                                        <span className="text-xs text-gray-400">—</span>
                                    ) : (
                                    <select
                                        className="w-28 text-xs border border-gray-300 rounded-md px-1 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        value={transaction["Grant Category"]}
                                        onChange={(e) =>
                                            handleGrantCategoryChange(
                                                transaction,
                                                e.target.value
                                            )
                                        }
                                    >
                                        <option value="">
                                            Select
                                        </option>
                                        {categories.map((category) => (
                                            <option
                                                key={category}
                                                value={category}
                                            >
                                                {category}
                                            </option>
                                        ))}
                                    </select>
                                    )}
                                </td>
                                <td className="px-2 py-2">
                                    {getLineItems(transaction).length > 0 ? (
                                        <span className="text-xs text-gray-400">—</span>
                                    ) : (
                                    <select
                                        className="w-28 text-xs border border-gray-300 rounded-md px-1 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
                                        value={
                                            transaction["Customized Category"]
                                        }
                                        onChange={(e) =>
                                            handleEditSubmit({
                                                ...transaction,
                                                "Customized Category":
                                                    e.target.value,
                                            })
                                        }
                                        disabled={[
                                            "Internal Bank Transfer",
                                            "Credit Card Payment",
                                        ].includes(
                                            transaction["Grant Category"]
                                        )}
                                    >
                                        <option value="N/A">N/A</option>
                                        {(() => {
                                            const selectedExpenseType = transaction["Grant Category"] || '';
                                            const typeSpecific = categoriesByExpenseType[selectedExpenseType] || [];
                                            const merged = [...new Set(typeSpecific)].filter(c => c !== 'N/A');
                                            return merged.map(
                                            (category) => (
                                                <option
                                                    key={category}
                                                    value={category}
                                                >
                                                    {category}
                                                </option>
                                            ));
                                        })()}
                                    </select>
                                    )}
                                </td>
                                {grantList.map((grant) => {
                                    const percentage = payment[grant] || "0";
                                    const isItemized = getLineItems(transaction).length > 0;
                                    return (
                                        <td key={grant} className="px-1 py-2 text-center border-l border-gray-200">
                                            {isItemized ? (
                                                <span className="text-xs text-gray-400">—</span>
                                            ) : (
                                            <PaymentPercentInput
                                                inputKey={`${transaction.ID}-${grant}-${percentage}`}
                                                grant={grant}
                                                grants={grantList}
                                                payment={payment}
                                                onChange={(updatedPayment) => handleEditDefaultPaymentSubmit({
                                                    ...transaction,
                                                    defaultPayment: updatedPayment,
                                                })}
                                                disabled={[
                                                    "Internal Bank Transfer",
                                                    "Credit Card Payment",
                                                ].includes(transaction["Grant Category"])}
                                            />
                                            )}
                                        </td>
                                    );
                                })}
                                <td className={`px-2 py-2 text-center sticky right-0 z-10 ${rowBgClass}`} style={{ position: 'sticky', right: 0, boxShadow: '-2px 0 4px rgba(0,0,0,0.05)' }}>
                                    <div className="flex items-center justify-center gap-1">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                            checked={transaction.Checked === "Yes"}
                                            onChange={(e) =>
                                                handleCheckboxChange(
                                                    transaction.ID,
                                                    e.target.checked
                                                )
                                            }
                                        />
                                        <button
                                            className={`p-1 rounded transition-colors ${transaction.Note ? 'text-amber-600 hover:bg-amber-50' : 'text-gray-400 hover:bg-gray-100'}`}
                                            onClick={() => handleEditNoteClick(transaction)}
                                            title={transaction.Note ? transaction.Note : 'Add note'}
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                        <button
                                            className={`p-1 rounded transition-colors ${getLineItems(transaction).length > 0 ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-400 hover:bg-gray-100'}`}
                                            onClick={() => toggleItemization(transaction.ID)}
                                            title={getLineItems(transaction).length > 0 ? `${getLineItems(transaction).length} line items` : 'Itemize transaction'}
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            {/* Full bank description sub-row - matches parent row background */}
                            <tr className={rowBgClass} style={{ borderBottom: expandedItemRows[transaction.ID] ? 'none' : '1px solid #e2e8f0' }}>
                                <td colSpan={colCount} className="pl-8 pr-4 py-1.5 text-xs text-gray-500">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>{transaction["Bank Description"]}</span>
                                        {getLineItems(transaction).length === 0 && Math.abs(paymentTotal - 100) > 0.01 && paymentTotal > 0 && (
                                            <span style={{ color: '#dc2626', fontWeight: 600, fontSize: '11px' }}>
                                                Payment total: {paymentTotal.toFixed(0)}% (must be 100%)
                                            </span>
                                        )}
                                        {getLineItems(transaction).length > 0 && (
                                            <span className="text-blue-600 font-medium text-xs">
                                                Itemized ({getLineItems(transaction).length} items)
                                            </span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                            {/* Line Items sub-rows */}
                            {expandedItemRows[transaction.ID] && (
                                <>
                                    {getLineItems(transaction).map((item, itemIdx) => {
                                        const itemPayment = item.payment || {};
                                        const itemPaymentTotal = grantList.reduce((sum, g) => sum + (parseFloat(itemPayment[g]) || 0), 0);
                                        return (
                                        <tr key={item.id} className="bg-blue-50 border-b border-blue-100">
                                            <td className="pl-8 pr-2 py-1.5 text-xs text-gray-500">
                                                <div className="flex items-center gap-1">
                                                    <span className="text-gray-400">#{itemIdx + 1}</span>
                                                    <input
                                                        type="text"
                                                        className="w-24 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                        defaultValue={item.name || ''}
                                                        placeholder="Name"
                                                        onBlur={(e) => updateLineItem(transaction, item.id, 'name', e.target.value)}
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="w-20 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                    defaultValue={item.amount}
                                                    placeholder="Amount"
                                                    onBlur={(e) => updateLineItem(transaction, item.id, 'amount', e.target.value)}
                                                />
                                            </td>
                                            {showBankColumns && (<><td></td><td></td><td></td></>)}
                                            <td className="px-2 py-1.5">
                                                <select
                                                    className="w-28 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                    value={item.grantCategory || ''}
                                                    onChange={(e) => updateLineItem(transaction, item.id, 'grantCategory', e.target.value)}
                                                >
                                                    <option value="">Select</option>
                                                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <select
                                                    className="w-28 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                    value={item.customizedCategory || 'N/A'}
                                                    onChange={(e) => updateLineItem(transaction, item.id, 'customizedCategory', e.target.value)}
                                                >
                                                    <option value="N/A">N/A</option>
                                                    {(() => {
                                                        const typeSpecific = categoriesByExpenseType[item.grantCategory || ''] || [];
                                                        const unassigned = categoriesByExpenseType['Unassigned'] || categoriesByExpenseType[''] || [];
                                                        const merged = [...new Set([...typeSpecific, ...unassigned])].filter(c => c !== 'N/A');
                                                        return (merged.length > 0 ? merged : customizedCategories.filter(c => c !== 'N/A')).map(c => (
                                                            <option key={c} value={c}>{c}</option>
                                                        ));
                                                    })()}
                                                </select>
                                            </td>
                                            {grantList.map(grant => (
                                                <td key={grant} className="px-1 py-1.5 text-center border-l border-blue-100">
                                                    <PaymentPercentInput
                                                        inputKey={`${item.id}-${grant}-${itemPayment[grant] || "0"}`}
                                                        grant={grant}
                                                        grants={grantList}
                                                        payment={itemPayment}
                                                        onChange={(newPayment) => updateLineItem(transaction, item.id, 'payment', newPayment)}
                                                    />
                                                </td>
                                            ))}
                                            <td className="px-2 py-1.5 text-center sticky right-0 z-10 bg-blue-50" style={{ position: 'sticky', right: 0, boxShadow: '-2px 0 4px rgba(0,0,0,0.05)' }}>
                                                <div className="flex items-center justify-center gap-1">
                                                    {Math.abs(itemPaymentTotal - 100) > 0.01 && itemPaymentTotal > 0 && (
                                                        <span className="text-red-500 text-xs font-medium">{itemPaymentTotal.toFixed(0)}%</span>
                                                    )}
                                                    <button
                                                        onClick={() => removeLineItem(transaction, item.id)}
                                                        className="p-0.5 text-red-400 hover:text-red-600 rounded"
                                                        title="Remove line item"
                                                    >
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                    <tr className="bg-blue-50" style={{ borderBottom: '2px solid #93c5fd' }}>
                                        <td colSpan={colCount} className="pl-8 pr-4 py-2">
                                            <button
                                                onClick={() => addLineItem(transaction)}
                                                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                Add Line Item
                                            </button>
                                        </td>
                                    </tr>
                                </>
                            )}
                            </React.Fragment>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Custom Period Popup */}
            {showCustomPeriodPopup && (
                <div className={"popup-overlay"}>
                    <div className={"popup"}>
                        <h3>Select Custom Period</h3>
                        <div className={"popup-content"}>
                            <div className={"date-input-group"}>
                                <label>Start Date</label>
                                <input
                                    type="date"
                                    value={customStartDate}
                                    onChange={(e) =>
                                        setCustomStartDate(e.target.value)
                                    }
                                />
                            </div>
                            <div className={"date-input-group"}>
                                <label>End Date</label>
                                <input
                                    type="date"
                                    value={customEndDate}
                                    onChange={(e) =>
                                        setCustomEndDate(e.target.value)
                                    }
                                />
                            </div>
                            <div className={"popup-buttons"}>
                                <button onClick={handleCustomPeriodSubmit}>
                                    Apply
                                </button>
                                <button
                                    onClick={() =>
                                        setShowCustomPeriodPopup(false)
                                    }
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Note Editing Popup */}
            {showEditNotePopup && transactionToEditNote && (
              <div className="popup-overlay">
                <div className="popup">
                  <h3>Edit Transaction Note</h3>
                  <div className="popup-content">
                    <div className="transaction-details">
                      <p><strong>Date:</strong> {new Date(transactionToEditNote['Date (UTC)']).toLocaleDateString()}</p>
                      <p><strong>Description:</strong> {transactionToEditNote['Bank Description']}</p>
                      <p><strong>Amount:</strong> ${Math.abs(parseFloat(transactionToEditNote.Amount)).toFixed(2)}</p>
                    </div>
                    <div className="note-textarea-container">
                      <label>Note:</label>
                      <textarea
                        value={transactionToEditNote.Note || ''}
                        onChange={(e) => setTransactionToEditNote({
                          ...transactionToEditNote,
                          Note: e.target.value
                        })}
                        placeholder="Add your notes here..."
                        rows={4}
                      />
                    </div>
                    <div className="popup-buttons">
                      <button onClick={handleSaveNote}>Save Note</button>
                      <button onClick={() => setShowEditNotePopup(false)}>Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Default Payment Popup - removed, inline editing used instead */}

            {/* Additional Transaction Tables */}
            <div className={"additional-tables"}>
                <div className={"section"}>
                    <h2>Rippling Payroll Transactions</h2>
                    <RipplingPayrollTable
                        ripplingPayrollTransactions={
                            [...ripplingPayrollTransactions, ...payrollRelatedExpenses.filter(t => !ripplingPayrollTransactions.some(r => r.ID === t.ID))]
                        }
                    />
                </div>

                <div className={"section"}>
                    <h2>Deposit Transactions</h2>
                    <DepositTable 
                        depositTransactions={depositTransactions}
                        initialFilterStartDate={customStartDate}
                        initialFilterEndDate={customEndDate}
                    />
                </div>
            </div>

            <ConfirmSubmitModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmCheckedRecords}
            />

            {showBulkCategoryPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Set Expense Type</h3>
                        <p className="text-sm text-gray-600 mb-3">
                            Apply this expense type to all unconfirmed transactions visible in the current view:
                        </p>
                        <select
                            className="input-field mb-4"
                            value={bulkCategoryValue}
                            onChange={(e) => setBulkCategoryValue(e.target.value)}
                        >
                            <option value="">Select expense type</option>
                            {categories.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                        <div className="flex justify-end gap-2">
                            <button
                                className="btn-secondary"
                                onClick={() => { setShowBulkCategoryPopup(false); setBulkCategoryValue(""); }}
                            >
                                Cancel
                            </button>
                            <button className="btn-primary" onClick={handleBulkSetCategory}>
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
const EditDefaultPaymentPopup = ({
    selectedTransaction,
    grantList,
    onSave,
    onCancel,
}) => {
    const [defaultPayment, setDefaultPayment] = useState(
        typeof selectedTransaction.defaultPayment === "string"
            ? JSON.parse(selectedTransaction.defaultPayment || "{}")
            : selectedTransaction.defaultPayment || {}
    );

    const handlePercentageChange = (grant, percentage) => {
        setDefaultPayment((prev) => ({
            ...prev,
            [grant]: percentage,
        }));
    };

    const handleSubmit = () => {
        const totalPercentage = Object.values(defaultPayment).reduce(
            (sum, value) => sum + parseFloat(value || 0),
            0
        );

        if (Math.abs(totalPercentage - 100) > 0.01) {
            alert(
                `Total percentage must equal 100%. Current total: ${totalPercentage.toFixed(
                    2
                )}%`
            );
            return;
        }
        onSave({ ...selectedTransaction, defaultPayment });
    };

    return (
        <div className="popup">
            <h3>Edit Payment</h3>
            {grantList.map((grant) => (
                <div key={grant}>
                    <label>{grant}</label>
                    <input
                        type="number"
                        value={defaultPayment[grant] || ""}
                        onChange={(e) =>
                            handlePercentageChange(grant, e.target.value)
                        }
                        min="0"
                        max="100"
                        step="0.01"
                    />
                </div>
            ))}
            <button onClick={handleSubmit}>Save</button>
            <button onClick={onCancel}>Cancel</button>
        </div>
    );
};
const CustomPeriodPopup = ({
    customStartDate,
    customEndDate,
    setCustomStartDate,
    setCustomEndDate,
    onApply,
    onCancel,
}) => {
    return (
        <div className="popup">
            <h3>Select Custom Period</h3>
            <div>
                <label>Start Date</label>
                <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                />
            </div>
            <div>
                <label>End Date</label>
                <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                />
            </div>
            <button onClick={onApply}>Apply</button>
            <button onClick={onCancel}>Cancel</button>
        </div>
    );
};

export default ExpenseTable;
